import { Draw, Transport, now } from 'tone';
import { Decoder, tools, Reader } from 'ts-ebml';
import EventEmitter from 'events';
import { isNumber, kebabCase, round } from 'lodash';
import WavEncoder from 'wav-encoder';

import * as transport from '../audio/transport';
import { downloadFile } from './recordingUtils';
import * as encoding from './encoding';
import * as sfx from '../audio/sfx';

const MAX_RECORDING_TIME = 120;
const MEDIA_RECORDER_SUPPORTED = 'MediaRecorder' in window;

export class Recorder extends EventEmitter {
    private state: 'notStarted' | 'starting' | 'started' | 'stopped' = 'notStarted';
    private recorderGain: GainNode;
    private recorderStream?: MediaStreamAudioDestinationNode;
    private recorder?: MediaRecorder;
    private recorderScriptProcessor?: ScriptProcessorNode;
    private recordingRawPointer?: number;
    private recordingRawDataLeft?: Float32Array;
    private recordingRawDataRight?: Float32Array;

    private recorderStartedAt: number | null = null;
    private countdownFinishedAt: number | null = null;
    private tickSchedule: NodeJS.Timeout | null = null;;

    public recording: Blob | null = null;
    private recordedDuration: number | null = null;

    private encoder: encoding.Encoder | null = null;
    public artistName: string | null = null;
    public trackName: string | null = null;
    public finalVideo: Blob | null = null;
    public finalPoster: Blob | null = null;
    public shareLink: string | null = null;

    constructor() {
        super();
        this.recorderGain = transport.nativeAudioContext.createGain();
        this.recorderGain.gain.value = 0;
        transport.dryDest.connect(this.recorderGain);
        if (MEDIA_RECORDER_SUPPORTED) {
            this.recorderStream = transport.nativeAudioContext.createMediaStreamDestination();
            this.recorder = new MediaRecorder(this.recorderStream.stream);
            this.recorderGain.connect(this.recorderStream);
        } else {
            this.recordingRawDataLeft = new Float32Array(MAX_RECORDING_TIME * transport.nativeAudioContext.sampleRate);
            this.recordingRawDataRight = new Float32Array(MAX_RECORDING_TIME * transport.nativeAudioContext.sampleRate);
            this.recordingRawPointer = 0;
        }
    }

    start() {
        if (this.state !== 'notStarted') return;

        if (this.recorder) {
            this.recorder.start();
        }

        let oneMeasureDur = Transport.toSeconds('1m');
        let oneQuarterDur = Transport.toSeconds('4n');

        this.recorderStartedAt = now();
        this.countdownFinishedAt = transport.nextMeasureContextTime();
        while (this.countdownFinishedAt - this.recorderStartedAt < oneMeasureDur * 3 / 4) {
            this.countdownFinishedAt += oneMeasureDur;
        }

        this.recorderGain.gain.setValueAtTime(0, this.recorderStartedAt);
        this.recorderGain.gain.setValueAtTime(0, this.countdownFinishedAt - 0.03);
        this.recorderGain.gain.linearRampToValueAtTime(1, this.countdownFinishedAt);

        this.state = 'starting';
        Draw.schedule(() => {
            if (this.state === 'starting') {
                this.state = 'started';
                if (!MEDIA_RECORDER_SUPPORTED) {
                    this.recorderScriptProcessor = transport.nativeAudioContext.createScriptProcessor(8192, 2, 2);
                    this.recorderScriptProcessor.onaudioprocess = evt => {
                        if (this.recordingRawDataLeft && this.recordingRawDataRight && this.recordingRawPointer! <= this.recordingRawDataLeft!.length - 8192) {
                            this.recordingRawDataLeft!.set(evt.inputBuffer.getChannelData(0), this.recordingRawPointer);
                            this.recordingRawDataRight!.set(evt.inputBuffer.getChannelData(1), this.recordingRawPointer);
                            this.recordingRawPointer! += 8192;
                        }
                    }
                    this.recorderGain.connect(this.recorderScriptProcessor);
                    this.recorderScriptProcessor.connect(transport.nativeAudioContext.destination);
                }
            }
        }, this.countdownFinishedAt);

        let beatTime = transport.nextQuarterContextTime();
        while (beatTime < now()) {
            beatTime += oneQuarterDur;
        }
        while (beatTime < this.countdownFinishedAt) {
            if (beatTime < this.countdownFinishedAt - oneQuarterDur) {
                sfx.triggerCountdownDown(beatTime);
            } else {
                sfx.triggerCountdownUp(beatTime);
            }
            beatTime += oneQuarterDur;
        }
        this.startTicking();
    }

    stop() {
        if (this.state !== 'started') return Promise.resolve();
        this.recordedDuration = now() - this.recorderStartedAt!;
        if (MEDIA_RECORDER_SUPPORTED) {
            return new Promise(res => {
                this.recorder!.addEventListener('dataavailable', async evt => {
                    this.stopTicking();
                    let blob = await injectMetadata(evt.data);
                    this.state = 'stopped';
                    this.recording = blob;
                    this.emit('stop');
                    res();
                }, { once: true });
                return this.recorder!.stop()
            });
        } else {
            this.recorderGain.disconnect(this.recorderScriptProcessor!);
            this.recorderScriptProcessor!.disconnect();
            this.stopTicking();
            let actualLeft = this.recordingRawDataLeft!.subarray(0, this.recordingRawPointer!);
            let actualRight = this.recordingRawDataRight!.subarray(0, this.recordingRawPointer!);
            return WavEncoder.encode({
                sampleRate: transport.nativeAudioContext.sampleRate,
                channelData: [actualLeft, actualRight]
            }).then((aBuf: ArrayBuffer) => {
                this.state = 'stopped';
                this.recording = new Blob([aBuf], { type: 'audio/wav' });
                this.recordingRawDataLeft = undefined;
                this.recordingRawDataRight = undefined;
                this.recordingRawPointer = undefined;
                this.emit('stop');
            });
        }
    }

    prepareEncoder() {
        if (!this.recording) return;
        this.encoder = new encoding.Encoder(
            this.recording!,
            this.recordedDuration! * 1000,
            (this.countdownFinishedAt! - this.recorderStartedAt!) * 1000 * 0.9
        );
    }

    startEncoder(artistName: string, trackName: string) {
        if (!this.recording) return;
        if (!this.encoder) this.prepareEncoder();
        this.artistName = artistName;
        this.trackName = trackName;
        this.encoder!.on('progress', p => this.emit('encoderProgress', p));
        Promise.all([
            this.encoder!.encode(artistName, trackName),
            this.obtainUploadUrls()])
            .then(async ([{ video, poster }, { id, videoUploadUrl, snapshotUploadUrl, metaUploadUrl }]) => {
                this.finalVideo = video;
                this.finalPoster = poster;
                this.shareLink = `${window.location.origin}/recordings/${id}`;
                let metadata = { artistName, trackName, uploadedAt: Date.now() };
                await Promise.all([
                    fetch(videoUploadUrl, { method: "PUT", body: video }),
                    fetch(snapshotUploadUrl, { method: "PUT", body: poster }),
                    fetch(metaUploadUrl, { method: "PUT", body: new Blob([JSON.stringify(metadata)], { type: 'application/json' }) })
                ]);
                this.emit('encoderDone');
            });
    }

    download() {
        if (!this.finalVideo) return;
        downloadFile(this.finalVideo, this.getFilename('mp4'));
    }

    getFilename(extension: string) {
        let artistFilename = kebabCase(this.artistName ?? 'unknown artist');
        let trackName = kebabCase(this.trackName ?? 'unknown title');
        return `${artistFilename}_${trackName}.${extension}`
    }

    dispose() {
        this.recording = null;
        if (this.encoder) {
            this.encoder.cancel();
            this.encoder = null;
        }
    }

    private startTicking() {
        this.tickSchedule = setInterval(() => {
            let oneQuarter = Transport.toSeconds('4n');
            if (this.state === 'starting') {
                let timeLeft = Math.floor((this.countdownFinishedAt! - now()) / oneQuarter);
                this.emit('preCountdown', timeLeft + 1);
            } else if (this.state === 'started') {
                let timeRecorded = round(now() - this.countdownFinishedAt!, 2);
                this.emit('recordingTimer', timeRecorded, MAX_RECORDING_TIME);
                if (now() > this.countdownFinishedAt! + MAX_RECORDING_TIME) {
                    this.stop();
                }
            }
        }, 100);
    }

    private stopTicking() {
        if (isNumber(this.tickSchedule)) {
            clearInterval(this.tickSchedule);
            this.tickSchedule = null;
        }
    }

    private obtainUploadUrls(): Promise<{ id: string, videoUploadUrl: string, snapshotUploadUrl: string, metaUploadUrl: string }> {
        return fetch('/.netlify/functions/requestUploadURL').then(r => r.json())
    }
}

const injectMetadata = function (blob: Blob) {
    if (blob.type.indexOf('webm') >= 0) {
        const decoder = new Decoder();
        const reader = new Reader();
        reader.logging = false;
        reader.drop_default_duration = false;
        return readAsArrayBuffer(blob).then((buffer) => {
            const elms = decoder.decode(buffer);
            elms.forEach((elm) => { reader.read(elm); });
            reader.stop();
            var refinedMetadataBuf = tools.makeMetadataSeekable(
                reader.metadatas, reader.duration, reader.cues);
            var body = buffer.slice(reader.metadataSize);
            const result = new Blob([refinedMetadataBuf, body],
                { type: blob.type });
            return result;
        });
    } else {
        return blob;
    }
}

const readAsArrayBuffer = (blob: Blob) => {
    return new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsArrayBuffer(blob);
        reader.onloadend = () => { resolve(reader.result as ArrayBuffer); };
        reader.onerror = (ev) => { reject(ev); };
    });
}